import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, X, Play, Square, Save } from "lucide-react";
import {
  fetchDumpers, addDumper, removeDumper,
  fetchShovels, addShovel, removeShovel,
  startShift, endShift, saveAssignments,
  fetchUsers, addUser, removeUser,
} from "@/api";

const SHIFT_NAMES = ["Morning Shift", "Evening Shift", "Night Shift", "General Shift"];

function FleetPanel({ title, items, onAdd, onRemove, inputTestId, addTestId, itemPrefix }) {
  const [val, setVal] = useState("");
  const submit = async () => {
    if (!val.trim()) return;
    try {
      await onAdd(val);
      setVal("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
  };
  return (
    <div className="border-2 border-black bg-white hard-shadow p-4 flex-1 min-w-[260px]">
      <h3 className="font-head text-lg font-bold uppercase mb-3">{title}</h3>
      <div className="flex gap-2 mb-3">
        <input
          data-testid={inputTestId}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="e.g. D-101"
          className="flex-1 h-14 border-2 border-black px-3 text-lg font-bold uppercase focus:outline-none focus:ring-2 focus:ring-[#FFB800]"
        />
        <button
          data-testid={addTestId}
          onClick={submit}
          className="h-14 w-14 border-2 border-black bg-[#FFB800] hard-shadow-sm pressable flex items-center justify-center"
        >
          <Plus size={24} strokeWidth={3} />
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <span
            key={it.id}
            data-testid={`${itemPrefix}-${it.number}`}
            className="inline-flex items-center gap-1 border-2 border-black bg-zinc-100 px-2 py-1 font-bold text-sm"
          >
            {it.number}
            <button
              data-testid={`remove-${itemPrefix}-${it.number}`}
              onClick={() => onRemove(it.id)}
              className="hover:text-[#FF3B30]"
            >
              <X size={14} strokeWidth={3} />
            </button>
          </span>
        ))}
        {items.length === 0 && <span className="text-sm text-zinc-400 font-medium">None added yet</span>}
      </div>
    </div>
  );
}

function UserPanel() {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("tripman");

  const load = useCallback(async () => {
    try {
      setUsers(await fetchUsers());
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!email.trim() || !password) return;
    try {
      await addUser({ email, password, name, role });
      setEmail("");
      setPassword("");
      setName("");
      setRole("tripman");
      toast.success("Login ID created");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create user");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete login ID ${u.email}?`)) return;
    try {
      await removeUser(u.id);
      toast.success(`Deleted ${u.email}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <div className="border-2 border-black bg-white hard-shadow p-4" data-testid="user-panel">
      <h3 className="font-head text-lg font-bold uppercase mb-3">Login IDs</h3>
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          data-testid="user-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="h-12 border-2 border-black px-3 font-bold flex-1 min-w-[180px] focus:outline-none"
        />
        <input
          data-testid="user-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          className="h-12 border-2 border-black px-3 font-bold flex-1 min-w-[140px] focus:outline-none"
        />
        <input
          data-testid="user-password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password (min 6)"
          className="h-12 border-2 border-black px-3 font-bold flex-1 min-w-[140px] focus:outline-none"
        />
        <select
          data-testid="user-role-select"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="h-12 border-2 border-black px-2 font-bold bg-white"
        >
          <option value="tripman">Trip Man</option>
          <option value="manager">Shift Manager</option>
        </select>
        <button
          data-testid="add-user-btn"
          onClick={submit}
          className="h-12 px-5 border-2 border-black bg-[#FFB800] hard-shadow-sm pressable font-head font-bold uppercase flex items-center gap-1"
        >
          <Plus size={18} strokeWidth={3} /> Add
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {users.map((u) => (
          <span
            key={u.id}
            data-testid={`user-row-${u.email}`}
            className="inline-flex items-center gap-2 border-2 border-black bg-zinc-100 px-2 py-1 font-bold text-sm"
          >
            {u.email}
            <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 border border-black ${
              u.role === "manager" ? "bg-[#FFB800]" : "bg-white"
            }`}>
              {u.role === "manager" ? "MANAGER" : "TRIP MAN"}
            </span>
            <button
              data-testid={`delete-user-${u.email}`}
              onClick={() => del(u)}
              className="hover:text-[#FF3B30]"
            >
              <X size={14} strokeWidth={3} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function ShiftManager({ activeShift, refreshShift }) {
  const [dumpers, setDumpers] = useState([]);
  const [shovels, setShovels] = useState([]);
  const [shiftName, setShiftName] = useState(SHIFT_NAMES[0]);
  const [rows, setRows] = useState({});
  const [fleetLoaded, setFleetLoaded] = useState(false);

  const loadFleet = useCallback(async () => {
    const [d, s] = await Promise.all([fetchDumpers(), fetchShovels()]);
    setDumpers(d);
    setShovels(s);
    setFleetLoaded(true);
  }, []);

  useEffect(() => {
    loadFleet();
  }, [loadFleet]);

  const activeShiftId = activeShift?.id;

  useEffect(() => {
    // re-seed the allotment form only when a different shift loads, not on every poll
    const map = {};
    (activeShift?.assignments || []).forEach((a) => {
      map[a.dumper_id] = {
        allotted: true,
        shovel_id: a.shovel_id,
        material: a.material,
        dumper_operator: a.dumper_operator || "",
        shovel_operator: a.shovel_operator || "",
      };
    });
    setRows(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeShiftId]);

  const reload = async () => {
    await Promise.all([loadFleet(), refreshShift()]);
  };

  const handleStart = async () => {
    try {
      await startShift(shiftName);
      toast.success(`${shiftName} started`);
      refreshShift();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start shift");
    }
  };

  const handleEnd = async () => {
    if (!window.confirm("End this shift? Trip counting will stop.")) return;
    try {
      await endShift(activeShift.id);
      toast.success("Shift ended. Report is available in Reports tab.");
      refreshShift();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to end shift");
    }
  };

  const setRow = (dumperId, patch) =>
    setRows((r) => ({
      ...r,
      [dumperId]: { allotted: false, shovel_id: "", material: "COAL", dumper_operator: "", shovel_operator: "", ...r[dumperId], ...patch },
    }));

  const handleSave = async () => {
    const assignments = Object.entries(rows)
      .filter(([, v]) => v.allotted && v.shovel_id)
      .map(([dumper_id, v]) => {
        const d = dumpers.find((x) => x.id === dumper_id);
        const s = shovels.find((x) => x.id === v.shovel_id);
        if (!d || !s) return null;
        return {
          dumper_id, dumper_number: d.number, shovel_id: s.id, shovel_number: s.number,
          material: v.material, dumper_operator: v.dumper_operator || "", shovel_operator: v.shovel_operator || "",
        };
      })
      .filter(Boolean);
    try {
      await saveAssignments(activeShift.id, assignments);
      toast.success(`Allotment saved: ${assignments.length} dumpers assigned`);
      refreshShift();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save allotment");
    }
  };

  return (
    <div className="space-y-6" data-testid="manager-screen">
      <div className="border-2 border-black bg-white hard-shadow p-4">
        <h3 className="font-head text-lg font-bold uppercase mb-3">Shift Control</h3>
        {!activeShift ? (
          <div className="flex flex-wrap gap-3 items-stretch">
            <select
              data-testid="shift-name-select"
              value={shiftName}
              onChange={(e) => setShiftName(e.target.value)}
              className="h-16 border-2 border-black px-3 text-lg font-bold bg-white focus:outline-none"
            >
              {SHIFT_NAMES.map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
            <button
              data-testid="start-shift-btn"
              onClick={handleStart}
              className="flex-1 min-w-[200px] h-16 border-2 border-black bg-[#FFB800] hard-shadow pressable font-head text-xl font-black uppercase flex items-center justify-center gap-2"
            >
              <Play size={22} strokeWidth={3} /> Start Shift
            </button>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div>
              <div className="font-head text-2xl font-black uppercase" data-testid="active-shift-name">
                {activeShift.name}
              </div>
              <div className="text-xs font-semibold text-zinc-500 uppercase">
                Started {new Date(activeShift.started_at).toLocaleString()}
              </div>
            </div>
            <button
              data-testid="end-shift-btn"
              onClick={handleEnd}
              className="h-16 px-6 border-2 border-black bg-[#FF3B30] text-white hard-shadow pressable font-head text-lg font-black uppercase flex items-center gap-2"
            >
              <Square size={20} strokeWidth={3} /> End Shift
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-4">
        <FleetPanel
          title="Dumpers"          items={dumpers}
          onAdd={async (n) => { await addDumper(n); loadFleet(); }}
          onRemove={async (id) => {
            try {
              await removeDumper(id);
              reload();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Failed to remove dumper");
            }
          }}
          inputTestId="dumper-number-input"
          addTestId="add-dumper-btn"
          itemPrefix="dumper-chip"
        />
        <FleetPanel
          title="Shovels"
          items={shovels}
          onAdd={async (n) => { await addShovel(n); loadFleet(); }}
          onRemove={async (id) => {
            try {
              await removeShovel(id);
              reload();
            } catch (e) {
              toast.error(e.response?.data?.detail || "Failed to remove shovel");
            }
          }}
          inputTestId="shovel-number-input"
          addTestId="add-shovel-btn"
          itemPrefix="shovel-chip"
        />
      </div>

      {activeShift && (
        <div className="border-2 border-black bg-white hard-shadow p-4" data-testid="allotment-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="font-head text-lg font-bold uppercase">Dumper Allotment</h3>
            <button
              data-testid="save-allotment-btn"
              onClick={handleSave}
              disabled={!fleetLoaded}
              className="h-14 px-6 border-2 border-black bg-[#FFB800] hard-shadow-sm pressable font-head text-base font-black uppercase flex items-center gap-2 disabled:opacity-50"
            >
              <Save size={18} strokeWidth={3} /> Save Allotment
            </button>
          </div>
          {dumpers.length === 0 || shovels.length === 0 ? (
            <p className="text-sm font-medium text-zinc-500">Add at least one dumper and one shovel above.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-2 border-black border-collapse text-left">
                <thead>
                  <tr className="bg-black text-white">
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Allot</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Dumper</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Dumper Operator</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Shovel</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Shovel Operator</th>
                    <th className="border border-black px-3 py-2 font-head text-sm uppercase">Material</th>
                  </tr>
                </thead>
                <tbody>
                  {dumpers.map((d) => {
                    const row = rows[d.id] || { allotted: false, shovel_id: "", material: "COAL", dumper_operator: "", shovel_operator: "" };
                    return (
                      <tr key={d.id} className={row.allotted ? "bg-[#FFF6DE]" : "bg-white"} data-testid={`allot-row-${d.number}`}>
                        <td className="border border-black px-3 py-2">
                          <input
                            type="checkbox"
                            data-testid={`allot-check-${d.number}`}
                            checked={row.allotted}
                            onChange={(e) => setRow(d.id, { allotted: e.target.checked })}
                            className="w-7 h-7 accent-[#FFB800]"
                          />
                        </td>
                        <td className="border border-black px-3 py-2 font-head text-xl font-black">{d.number}</td>
                        <td className="border border-black px-3 py-2">
                          <input
                            data-testid={`dumper-operator-input-${d.number}`}
                            value={row.dumper_operator}
                            disabled={!row.allotted}
                            onChange={(e) => setRow(d.id, { dumper_operator: e.target.value })}
                            placeholder="Operator name"
                            className="h-12 w-full min-w-[140px] border-2 border-black px-2 font-bold bg-white disabled:opacity-40"
                          />
                        </td>
                        <td className="border border-black px-3 py-2">
                          <select
                            data-testid={`shovel-select-${d.number}`}
                            value={row.shovel_id}
                            disabled={!row.allotted}
                            onChange={(e) => setRow(d.id, { shovel_id: e.target.value })}
                            className="h-12 border-2 border-black px-2 font-bold bg-white disabled:opacity-40 w-full min-w-[140px]"
                          >
                            <option value="">-- Shovel --</option>
                            {shovels.map((s) => (
                              <option key={s.id} value={s.id}>{s.number}</option>
                            ))}
                          </select>
                        </td>
                        <td className="border border-black px-3 py-2">
                          <input
                            data-testid={`shovel-operator-input-${d.number}`}
                            value={row.shovel_operator}
                            disabled={!row.allotted}
                            onChange={(e) => setRow(d.id, { shovel_operator: e.target.value })}
                            placeholder="Operator name"
                            className="h-12 w-full min-w-[140px] border-2 border-black px-2 font-bold bg-white disabled:opacity-40"
                          />
                        </td>
                        <td className="border border-black px-3 py-2">
                          <div className="flex gap-2">
                            {["COAL", "OB"].map((m) => (
                              <button
                                key={m}
                                data-testid={`material-${m.toLowerCase()}-${d.number}`}
                                disabled={!row.allotted}
                                onClick={() => setRow(d.id, { material: m })}
                                className={`h-12 px-4 border-2 border-black font-head font-bold uppercase pressable disabled:opacity-40 ${
                                  row.material === m
                                    ? m === "COAL" ? "bg-black text-white" : "bg-[#FFB800] text-black"
                                    : "bg-white"
                                }`}
                              >
                                {m}
                              </button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
